// SPDX-License-Identifier: AGPL-3.0-or-later

import {MessageUsername} from '@app/features/channel/components/MessageUsername';
import type {Guild} from '@app/features/guild/models/Guild';
import type {GuildMember} from '@app/features/member/models/GuildMember';
import {SafeMarkdown} from '@app/features/messaging/components/markdown';
import {
	MarkdownContext,
	type MarkdownParseOptions,
} from '@app/features/messaging/components/markdown/renderers/RendererTypes';
import type {Message} from '@app/features/messaging/models/MessagingMessage';
import {isDeleteTombstone} from '@app/features/messaging/utils/MessageTombstoneUtils';
import styles from '@app/features/theme/styles/Message.module.css';
import tombstoneStyles from '@app/features/theme/styles/MessageDeleteTombstone.module.css';
import type {User} from '@app/features/user/models/User';
import * as NicknameUtils from '@app/features/user/utils/NicknameUtils';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {type ReactNode, useMemo} from 'react';

interface MeEmoteContentProps {
	message: Message;
	author: User;
	guild?: Guild;
	member?: GuildMember;
	innerContent: string;
	markdownOptions: MarkdownParseOptions;
	isPreview: boolean;
	previewColor?: string;
	previewName?: string;
	className?: string;
	editedLabel?: ReactNode;
}

export const MeEmoteContent = observer(function MeEmoteContent({
	message,
	author,
	guild,
	member,
	innerContent,
	markdownOptions,
	isPreview,
	previewColor,
	previewName,
	className,
	editedLabel,
}: MeEmoteContentProps) {
	const displayName = previewName || NicknameUtils.getNickname(author, guild?.id, message.channelId);
	const copyText = `* ${displayName} ${innerContent}`;
	const emoteMarkdownOptions = useMemo(
		() => ({
			...markdownOptions,
			context: MarkdownContext.STANDARD_WITHOUT_JUMBO,
		}),
		[markdownOptions],
	);
	return (
		<span
			className={clsx(styles.meEmote, className)}
			data-search-highlight-scope="message"
			data-message-copy-block="true"
			data-message-copy-text={copyText}
			data-flx="channel.me-emote-content.me-emote"
		>
			<span className={styles.meEmoteAsterisk} aria-hidden="true" data-flx="channel.me-emote-content.asterisk">
				{'* '}
			</span>
			<MessageUsername
				user={author}
				message={message}
				guild={guild}
				member={member}
				className={clsx(styles.messageUsername, styles.meEmoteUsername)}
				isPreview={isPreview}
				previewColor={previewColor}
				previewName={previewName}
				data-flx="channel.me-emote-content.message-username"
			/>
			<span
				className={clsx(styles.meEmoteAction, isDeleteTombstone(message) && tombstoneStyles.tombstoneAction)}
				data-flx="channel.me-emote-content.action"
			>
				{' '}
				<SafeMarkdown
					content={innerContent}
					options={emoteMarkdownOptions}
					data-flx="channel.me-emote-content.safe-markdown"
				/>
			</span>
			{editedLabel}
		</span>
	);
});
